import React, { memo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import IconProvider from '../ui/IconProvider';
import { getCachedLogicFlows } from '@/lib/stores/logicFlowStore';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useNavigate } from 'react-router-dom';
import CardBase from '../cards/CardBase';

interface LogicFlowNodeData {
  itemId: string;
  itemType: 'flow';
  label: string;
  expanded?: boolean;
  onToggleExpand?: () => void;
  [key: string]: any;
}

export const LogicFlowNodeExpanded = memo(({ data, selected }: NodeProps) => {
  const { selectedGame } = useSimplifiedUserData();
  const navigate = useNavigate();
  const nodeData = data as LogicFlowNodeData;
  
  // Get the actual logic flow data
  const flows = selectedGame?.id ? getCachedLogicFlows(selectedGame.id) : [];
  const flow = flows.find(f => f.id === nodeData.itemId);
  
  // Count nodes and edges by type
  const nodeCount = flow?.data?.nodes?.length || 0;
  const edgeCount = flow?.data?.edges?.length || 0;
  
  // Count node types
  const objectNodes = flow?.data?.nodes?.filter(n => n.type === 'object' || n.type === 'objectExpanded') || [];
  const dialogNodes = flow?.data?.nodes?.filter(n => n.type === 'dialog' || n.type === 'dialogExpanded') || [];
  const flowNodes = flow?.data?.nodes?.filter(n => n.type === 'flow' || n.type === 'flowExpanded') || [];
  
  const handleOpenInTab = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (flow?.id) {
      navigate(`/logic/${flow.id}`);
    }
  };
  
  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    nodeData.onToggleExpand?.();
  };
  
  return (
    <CardBase
    shadowType="black"
    shadowColor={"#000000"}
    backgroundColor={"#ffffff"}
    border={true}
    borderColor={"#000000"}
    className="min-w-[280px] max-w-[400px]"
    >
      {/* Connection Handles */}
      <Handle 
        type="target" 
        position={Position.Top}
        id="top"
        className="w-3 h-3 !bg-primary"
      />
      <Handle 
        type="source" 
        position={Position.Bottom}
        id="bottom"
        className="w-3 h-3 !bg-primary"
      />
      <Handle 
        type="target" 
        position={Position.Left}
        id="left"
        className="w-3 h-3 !bg-primary"
      />
      <Handle 
        type="source" 
        position={Position.Right}
        id="right"
        className="w-3 h-3 !bg-primary"
      />
      
      {/* Header with icon and actions */}
      <div 
        className="flex items-center gap-2 px-3 py-2 rounded-t-md bg-gradient-to-r from-blue-500 to-cyan-500"
      >
        <div className="flex items-center justify-center w-6 h-6 rounded bg-white bg-opacity-20">
          <IconProvider iconstring="fa-sitemap" size={14} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {nodeData.label}
          </div>
        </div>
        <button
          onClick={handleToggleExpand}
          className="w-6 h-6 flex items-center justify-center rounded hover:bg-white hover:bg-opacity-20 transition-colors"
          title="Collapse"
        >
          <IconProvider iconstring="fa-compress" size={12} className="text-white" />
        </button>
        <button
          onClick={handleOpenInTab}
          className="w-6 h-6 flex items-center justify-center rounded hover:bg-white hover:bg-opacity-20 transition-colors"
          title="Open in tab"
        >
          <IconProvider iconstring="fa-external-link-alt" size={12} className="text-white" />
        </button>
      </div>
      
      {/* Body with detailed information */}
      <div className="px-3 py-3 space-y-2">
        {/* Description */}
        {flow?.description && (
          <div className="text-xs text-muted-foreground pb-2 border-b">
            {flow.description}
          </div>
        )}
        
        {/* Statistics */}
        <div className="space-y-1">
          <div className="text-xs font-semibold text-foreground">Statistics</div>
          
          {nodeCount > 0 && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <IconProvider iconstring="fa-circle-nodes" size={10} />
              <span className="truncate">{nodeCount} total node(s)</span>
            </div>
          )}
          
          {edgeCount > 0 && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <IconProvider iconstring="fa-link" size={10} />
              <span className="truncate">{edgeCount} connection(s)</span>
            </div>
          )}
        </div>
        
        {/* Node breakdown */}
        {nodeCount > 0 && (
          <div className="space-y-1 pt-1 border-t">
            <div className="text-xs font-semibold text-foreground">Node Types</div>
            
            {objectNodes.length > 0 && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <IconProvider iconstring="fa-cube" size={10} />
                <span className="truncate">{objectNodes.length} object(s)</span>
              </div>
            )}
            
            {dialogNodes.length > 0 && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <IconProvider iconstring="fa-comments" size={10} />
                <span className="truncate">{dialogNodes.length} dialog(s)</span>
              </div>
            )}
            
            {flowNodes.length > 0 && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <IconProvider iconstring="fa-sitemap" size={10} />
                <span className="truncate">{flowNodes.length} flow(s)</span>
              </div>
            )}
          </div>
        )}
        
        {!nodeCount && !edgeCount && (
          <div className="text-xs text-muted-foreground italic text-center py-2">
            Empty logic flow
          </div>
        )}
        
        {/* Folder info */}
        {flow?.folder && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground pt-1 border-t">
            <IconProvider iconstring="fa-folder" size={10} />
            <span className="truncate">In folder</span>
          </div>
        )}
      </div>
    </CardBase>
  );
});

LogicFlowNodeExpanded.displayName = 'LogicFlowNodeExpanded';

